import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Users, Store, CalendarDays, BookOpen, Check } from "lucide-react";

import AccountApprovalsTab from "./admin/AccountApprovalsTab";
import ContentApprovalsTab from "./admin/ContentApprovalsTab";
import PhotosTab from "./admin/PhotosTab";
import UsersTab from "./admin/UsersTab";
import ContentTab from "./admin/ContentTab";
import SettingsTab from "./admin/SettingsTab";
import InvitesTab from "./admin/InvitesTab";
import PromoCodesTab from "./admin/PromoCodesTab";
import DeletedAccountsTab from "./admin/DeletedAccountsTab";
import AnalyticsTab from "./admin/AnalyticsTab";
import CampaignsTab from "./admin/CampaignsTab";
import EmailUserDialog from "./admin/EmailUserDialog";
import ContentDetailDialog from "./admin/ContentDetailDialog";
import UserDetailDialog from "./admin/UserDetailDialog";

// Thin orchestrator for the admin dashboard: owns data loading, action
// handlers, and tab routing. Each tab panel + detail dialog lives in its own
// focused module under /pages/admin/ so edits land in a single file instead
// of scrolling through a 1500-line monolith.
export default function Admin() {
  const { refresh } = useAuth();
  const navigate = useNavigate();

  // Which top-level dashboard section is active.
  const [tab, setTab] = useState("approvals");
  // Users tab sub-state (role filter + search box).
  const [userTab, setUserTab] = useState("parent");
  const [userSearch, setUserSearch] = useState("");

  // Primary data buckets loaded by `load()` from the admin endpoints.
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [allVendors, setAllVendors] = useState([]);
  const [pendingContent, setPendingContent] = useState({ events: [], listings: [] });
  const [users, setUsers] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [resources, setResources] = useState([]);
  const [events, setEvents] = useState([]);
  const [pendingPhotos, setPendingPhotos] = useState([]);

  // Content-tab publisher forms.
  const [ann, setAnn] = useState({ title: "", body: "", pinned: false });
  const [res, setRes] = useState({ title: "", description: "", kind: "printable", url: "" });
  const resFile = useRef(null);

  // Settings-tab form state (populated by `load()`).
  const [settings, setSettings] = useState({ community_name: "Compass Kids", welcome_message: "", announcement_banner: "", registration_open: true });

  // Dialog state.
  const [detailUser, setDetailUser] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [contentDetail, setContentDetail] = useState(null); // { type, data }
  const [contentDetailLoading, setContentDetailLoading] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: "", body: "" });
  const [emailSending, setEmailSending] = useState(false);

  const load = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/pending-accounts").then((r) => setPending(r.data)).catch(() => {});
    api.get("/admin/vendors?status=all").then((r) => setAllVendors(r.data)).catch(() => {});
    api.get("/admin/pending-content").then((r) => setPendingContent(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/admin/pending-photos").then((r) => setPendingPhotos(r.data)).catch(() => {});
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    api.get("/resources").then((r) => setResources(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(load, []);

  // ── Review / moderation actions ───────────────────────────────────────────
  // All share the same shape: optional rejection reason prompt, action POST,
  // success toast, then a full `load()` to refresh every panel in one go.

  const review = async (userId, action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Reason for rejection (optional — the user will see this in the email):", "") ?? "";
    }
    try {
      const qs = action === "reject" && reason ? `&reason=${encodeURIComponent(reason)}` : "";
      await api.post(`/admin/accounts/${userId}/review?action=${action}${qs}`);
      toast.success(`Account ${action}d`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Action failed"));
    }
  };

  const reviewEvent = async (id, action) => {
    let reason = "";
    if (action === "reject") reason = window.prompt("Reason for rejection (optional — sent to the event creator):", "") ?? "";
    try {
      const qs = action === "reject" && reason ? `&reason=${encodeURIComponent(reason)}` : "";
      await api.post(`/admin/events/${id}/review?action=${action}${qs}`);
      toast.success(`Event ${action}d`);
      load();
    } catch (e) { toast.error(errMsg(e, "Action failed")); }
  };

  const reviewEventEdit = async (id, action) => {
    let reason = "";
    if (action === "reject") reason = window.prompt("Reason for rejecting these edits (optional):", "") ?? "";
    try {
      const qs = action === "reject" && reason ? `&reason=${encodeURIComponent(reason)}` : "";
      await api.post(`/admin/event-edits/${id}/review?action=${action}${qs}`);
      toast.success(`Edit ${action}d`);
      load();
    } catch (e) { toast.error(errMsg(e, "Action failed")); }
  };

  const reviewProfileEdit = async (id, action) => {
    let reason = "";
    if (action === "reject") reason = window.prompt("Reason for rejecting these profile edits (optional — sent to the submitter):", "") ?? "";
    try {
      const qs = action === "reject" && reason ? `&reason=${encodeURIComponent(reason)}` : "";
      await api.post(`/admin/role-profile-edits/${id}/review?action=${action}${qs}`);
      toast.success(`Profile edit ${action}d`);
      load();
    } catch (e) { toast.error(errMsg(e, "Action failed")); }
  };

  const reviewListing = async (id, action) => {
    let reason = "";
    if (action === "reject") reason = window.prompt("Reason for rejection (optional — sent to the seller):", "") ?? "";
    try {
      const qs = action === "reject" && reason ? `&reason=${encodeURIComponent(reason)}` : "";
      await api.post(`/admin/marketplace/${id}/review?action=${action}${qs}`);
      toast.success(`Listing ${action}d`);
      load();
    } catch (e) { toast.error(errMsg(e, "Action failed")); }
  };

  const reviewPhoto = async (uid, action) => {
    let reason = "";
    if (action === "reject") reason = window.prompt("Reason for rejection (optional — sent to the user):", "") ?? "";
    try {
      const qs = action === "reject" && reason ? `&reason=${encodeURIComponent(reason)}` : "";
      await api.post(`/admin/users/${uid}/photo/review?action=${action}${qs}`);
      toast.success(`Photo ${action}d`);
      load();
    } catch (e) { toast.error(errMsg(e, "Action failed")); }
  };

  // ── User-row actions ──────────────────────────────────────────────────────

  const setSubscription = async (userId, action) => {
    try {
      await api.post(`/admin/users/${userId}/subscription?action=${action}`);
      toast.success(action === "activate" ? "Subscription activated (no payment)" : "Subscription deactivated");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Action failed"));
    }
  };

  const deleteUser = async (u) => {
    const label = u.email || u.name || u.user_id;
    if (!window.confirm(`Permanently delete ${label}?\n\nThis wipes their account and every event, listing, RSVP, message, subscription and file linked to them. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`${label} deleted`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Delete failed"));
    }
  };

  const openUserDetail = async (u) => {
    setDetailUser(u);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.user_id}/detail`);
      setDetailData(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load user detail"));
      setDetailUser(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleFreeAccess = async (userId, action) => {
    const verb = action === "grant" ? "grant" : "revoke";
    const confirmMsg = action === "grant"
      ? "Grant this user full access to Compass Kids without any subscription payment?"
      : "Revoke free access? They'll be routed to the payment page on their next login.";
    if (!window.confirm(confirmMsg)) return;
    try {
      await api.post(`/admin/users/${userId}/free-access?action=${action}`);
      toast.success(action === "grant" ? "Free access granted" : "Free access revoked");
      // Refresh the detail dialog + user list so the badges update instantly.
      const { data } = await api.get(`/admin/users/${userId}/detail`);
      setDetailData(data);
      api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    } catch (e) {
      toast.error(errMsg(e, `Could not ${verb} free access`));
    }
  };

  const openContentDetail = async (type, data) => {
    setContentDetail({ type, data });
    // Marketplace listing cards only show summary — fetch the full listing so
    // the detail view can show photos and the long description.
    if (type === "listing" && data?.listing_id && !(data.photos || data.description)) {
      setContentDetailLoading(true);
      try {
        const resp = await api.get(`/marketplace/${data.listing_id}`);
        setContentDetail({ type, data: resp.data });
      } catch (e) {
        console.warn("Could not fetch full listing", e);
      } finally {
        setContentDetailLoading(false);
      }
    }
  };

  const resendStepEmail = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.user_id}/resend-email`);
      toast.success(data?.message || "Email resent");
    } catch (e) {
      toast.error(errMsg(e, "Could not resend email"));
    }
  };

  const openEmailDialog = (u) => {
    setEmailFor(u);
    setEmailForm({ subject: "", body: "" });
  };

  const sendCustomEmail = async () => {
    if (!emailFor) return;
    const subject = emailForm.subject.trim();
    const body = emailForm.body.trim();
    if (!subject || !body) {
      toast.error("Subject and message are required");
      return;
    }
    setEmailSending(true);
    try {
      await api.post(`/admin/users/${emailFor.user_id}/email`, { subject, body });
      toast.success(`Email sent to ${emailFor.email}`);
      setEmailFor(null);
      setEmailForm({ subject: "", body: "" });
    } catch (e) {
      toast.error(errMsg(e, "Could not send email"));
    } finally {
      setEmailSending(false);
    }
  };

  // ── Content / Settings publishers ─────────────────────────────────────────

  const postAnnouncement = async () => {
    try {
      await api.post("/announcements", ann);
      toast.success("Announcement posted");
      setAnn({ title: "", body: "", pinned: false });
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not post"));
    }
  };

  const postResource = async () => {
    try {
      if (res.kind === "link" && !(res.url || "").trim()) {
        toast.error("Please enter a URL");
        return;
      }
      const fd = new FormData();
      const file = res.kind !== "link" ? resFile.current?.files?.[0] : null;
      if (file) fd.append("file", file);
      const qs = `title=${encodeURIComponent(res.title)}&description=${encodeURIComponent(res.description)}&kind=${res.kind}&url=${encodeURIComponent(res.kind === "link" ? (res.url || "").trim() : "")}`;
      await api.post(`/resources?${qs}`, fd);
      toast.success("Resource published");
      setRes({ title: "", description: "", kind: "printable", url: "" });
      if (resFile.current) resFile.current.value = "";
      load();
    } catch (e) {
      toast.error(errMsg(e, "Could not publish"));
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", settings);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(errMsg(e, "Could not save settings"));
    }
  };

  // View-as-role (not currently rendered — kept so the API wiring stays
  // intact for the sibling admin ribbon).
  const viewAs = async (role) => {
    try {
      await api.post("/auth/admin-view-as", { role });
      const fresh = await refresh();
      toast.success(`Now viewing Compass Kids as ${role.replace(/_/g, " ")}`);
      if (fresh) navigate(dashboardFor(fresh));
    } catch (e) {
      toast.error(errMsg(e, "Could not switch role"));
    }
  };

  const contentApprovalsCount =
    pendingContent.events.length +
    pendingContent.listings.length +
    (pendingContent.event_edits?.length || 0) +
    (pendingContent.profile_edits?.length || 0);

  const TABS = [
    ["approvals", `Account approvals${pending.length ? ` (${pending.length})` : ""}`],
    ["content_approvals", `Content approvals${contentApprovalsCount ? ` (${contentApprovalsCount})` : ""}`],
    ["photos", `Profile photos${pendingPhotos.length ? ` (${pendingPhotos.length})` : ""}`],
    ["users", "Users"],
    ["invites", "Invites"],
    ["promo_codes", "Vouchers"],
    ["deleted_accounts", "Deleted accounts"],
    ["analytics", "Analytics"],
    ["campaigns", "Campaigns"],
    ["content", "Content"],
    ["settings", "Settings"],
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-dashboard">
      <div className="mb-6 fade-in">
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-sage-dark" /> Admin console
        </p>
        <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1">Platform management</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {[
            { label: "Total users", value: stats.users, icon: Users },
            { label: "Pending vendors", value: stats.vendors_pending, icon: Store },
            { label: "Events", value: stats.events, icon: CalendarDays },
            { label: "RSVPs", value: stats.rsvps, icon: Check },
          ].map((s) => (
            <div key={s.label} className="bg-white/70 border border-sage/20 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
              <span className="w-10 h-10 rounded-xl bg-sage-light flex items-center justify-center shrink-0">
                <s.icon className="w-5 h-5 text-sage-dark" />
              </span>
              <div>
                <div className="text-xl font-black font-heading text-forest">{s.value}</div>
                <div className="text-[11px] text-forest/60 font-semibold">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 mb-6 border-b border-sage/20 overflow-x-auto">
        {TABS.map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-bold rounded-t-xl whitespace-nowrap transition-colors ${tab === key ? "bg-white/80 text-forest border border-b-0 border-sage/20" : "text-forest/50 hover:text-forest"}`}
            data-testid={`admin-tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "approvals" && (
        <AccountApprovalsTab
          pending={pending}
          allVendors={allVendors}
          review={review}
          openUserDetail={openUserDetail}
        />
      )}

      {tab === "content_approvals" && (
        <ContentApprovalsTab
          pendingContent={pendingContent}
          reviewEvent={reviewEvent}
          reviewEventEdit={reviewEventEdit}
          reviewProfileEdit={reviewProfileEdit}
          reviewListing={reviewListing}
          openContentDetail={openContentDetail}
        />
      )}

      {tab === "photos" && (
        <PhotosTab
          pendingPhotos={pendingPhotos}
          reviewPhoto={reviewPhoto}
          openUserDetail={openUserDetail}
        />
      )}

      {tab === "users" && (
        <UsersTab
          users={users}
          userTab={userTab}
          setUserTab={setUserTab}
          userSearch={userSearch}
          setUserSearch={setUserSearch}
          openUserDetail={openUserDetail}
          setSubscription={setSubscription}
          openEmailDialog={openEmailDialog}
          resendStepEmail={resendStepEmail}
          deleteUser={deleteUser}
        />
      )}

      {tab === "invites" && <InvitesTab />}

      {tab === "promo_codes" && <PromoCodesTab />}

      {tab === "deleted_accounts" && <DeletedAccountsTab />}

      {tab === "analytics" && <AnalyticsTab />}
      {tab === "campaigns" && <CampaignsTab />}

      {tab === "content" && (
        <ContentTab
          announcements={announcements}
          resources={resources}
          events={events}
          ann={ann}
          setAnn={setAnn}
          postAnnouncement={postAnnouncement}
          res={res}
          setRes={setRes}
          resFile={resFile}
          postResource={postResource}
          load={load}
        />
      )}

      {tab === "settings" && (
        <SettingsTab
          settings={settings}
          setSettings={setSettings}
          saveSettings={saveSettings}
        />
      )}

      <EmailUserDialog
        emailFor={emailFor}
        setEmailFor={setEmailFor}
        emailForm={emailForm}
        setEmailForm={setEmailForm}
        emailSending={emailSending}
        sendCustomEmail={sendCustomEmail}
      />

      <ContentDetailDialog
        contentDetail={contentDetail}
        setContentDetail={setContentDetail}
        contentDetailLoading={contentDetailLoading}
        reviewEvent={reviewEvent}
        reviewEventEdit={reviewEventEdit}
        reviewListing={reviewListing}
      />

      <UserDetailDialog
        detailUser={detailUser}
        detailData={detailData}
        detailLoading={detailLoading}
        setDetailUser={setDetailUser}
        setDetailData={setDetailData}
        toggleFreeAccess={toggleFreeAccess}
      />
    </div>
  );
}
